import React from "react";
import { Link } from "react-router-dom";
import DismissibleIntro from "@/components/DismissibleIntro";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { PhotoImg, authedFileUrl, downloadFile, FURNISH_STYLES } from "@/components/propertyDetail/helpers";
import {
  UploadSimple, Sparkle, Trash, DownloadSimple, Spinner, Armchair,
  House, Image as ImageIcon,
} from "@phosphor-icons/react";
import { usePropertyDetail } from "../context";

export default function PiantaTab() {
  const {
    id, photos, setPhotos, activePlan, activePlanId, setActivePlanId, planVertical,
    planStyle, setPlanStyle, furnishingId, onFurnishPlan, onDeletePhoto,
    planInputRef, onUploadPlan, onPlanFilePick,
  } = usePropertyDetail();
  const floorPlans = photos.filter((p) => p.photo_kind === "floor_plan");
  // PianTab non gestisce onDeletePhoto custom: usa quello del parent
  return (
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4">
          <div className="card-flat p-6">
            <DismissibleIntro storageKey="propertydetail:pianta-intro" className="mb-3">
              <span className="overline">Pianta arredata</span>
              <h3 className="font-editorial text-2xl mt-2 mb-3">Dalla piantina al sogno.</h3>
              <p className="text-sm text-[#6B6A65] mb-2">
                Carica la <strong>pianta catastale</strong>. Pronto genera una versione 2D arredata, vista dall'alto, stile editoriale. <strong className="text-[#1C1C1A]">Muri e scale invariati.</strong>
              </p>
            </DismissibleIntro>

            <div className="text-xs uppercase tracking-[0.18em] text-[#6B6A65] mb-2">Stile arredo</div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 mb-6 sm:flex-wrap sm:overflow-visible">
              {FURNISH_STYLES.map((s) => (
                <button
                  key={s.v}
                  onClick={() => setPlanStyle(s.v)}
                  className={`shrink-0 px-3.5 py-1.5 text-xs rounded-full border transition-all ${planStyle === s.v ? "bg-[#1C1C1A] text-white border-[#1C1C1A]" : "bg-white text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                  data-testid={`plan-style-${s.v}`}
                >
                  {s.l}
                </button>
              ))}
            </div>

            <input ref={planInputRef} type="file" accept="image/*" multiple onChange={onUploadPlan} className="hidden" data-testid="plan-file-input" />
            <button onClick={onPlanFilePick} className="btn-primary w-full justify-center" data-testid="upload-plan-btn">
              <UploadSimple size={16} /> Carica planimetria
            </button>
            <Link
              to={`/dashboard/galleria?import=${id}&type=plan`}
              className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[12.5px] text-[#7C8B6E] hover:text-[#1C1C1A] font-semibold py-2 border border-[#E5E7E0] hover:border-[#7C8B6E] rounded-md transition-colors"
              data-testid="plan-from-gallery"
            >
              <ImageIcon size={13} weight="fill" /> Scegli da galleria
            </Link>
            <p className="mt-4 text-[11px] text-[#6B6A65] leading-relaxed">
              <strong className="text-[#1C1C1A]">Per il miglior risultato:</strong> pianta <strong>nitida</strong>, scritte leggibili (CUCINA, BAGNO, CAMERA, TERRAZZO). Puoi caricarne anche <strong>più di una</strong> (es. piano terra + primo piano).
            </p>
          </div>
        </div>

        <div className="lg:col-span-8">
          {activePlan ? (
            <>
              {activePlan.furnished_plan_path ? (
                <BeforeAfterSlider
                  beforeSrc={authedFileUrl(activePlan.original_path)}
                  afterSrc={authedFileUrl(activePlan.furnished_plan_path)}
                  height="h-[58vh]"
                  rotate={planVertical ? -90 : 0}
                  fit="contain"
                />
              ) : (
                <div className="card-flat relative h-[58vh] overflow-hidden bg-white" data-testid="plan-preview-original">
                  <img src={authedFileUrl(activePlan.original_path)} alt="" className="w-full h-full object-contain" style={planVertical ? { transform: "rotate(-90deg)" } : undefined} />
                  <span className="absolute top-4 left-4 overline bg-[#FFFFFF]/80 px-3 py-1">Pianta originale</span>
                </div>
              )}
              {/* iter 261: bottoni allineati allo stile delle Foto —
                  primario full-width, riga sotto azioni compatte. */}
              <div className="mt-3 space-y-2" data-testid="active-plan-actions">
                <button
                  onClick={() => onFurnishPlan(activePlan)}
                  disabled={furnishingId === activePlan.id}
                  className="w-full bg-[#1C1C1A] hover:bg-black text-white py-2.5 px-3 rounded-full text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                  data-testid="furnish-plan-btn"
                >
                  {furnishingId === activePlan.id ? (<><Spinner size={15} className="animate-spin" /> Sto arredando…</>)
                    : activePlan.furnished_plan_path ? (<><Sparkle size={15} weight="fill" /> Rigenera ({planStyle})</>)
                    : (<><Sparkle size={15} weight="fill" /> Arreda la pianta ({planStyle})</>)}
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                  {activePlan.furnished_plan_path && (
                    <button
                      onClick={() => downloadFile(activePlan.furnished_plan_path, `pronto-pianta-${activePlan.id}.jpg`)}
                      className="inline-flex items-center gap-1.5 text-[12.5px] text-[#1C1C1A] hover:text-[#7C8B6E] font-medium px-2.5 py-1.5 transition-colors"
                      data-testid="download-plan-btn"
                    >
                      <DownloadSimple size={14} weight="bold" /> Scarica
                    </button>
                  )}
                  <button
                    onClick={() => onDeletePhoto(activePlan)}
                    className="inline-flex items-center gap-1.5 text-[12.5px] text-[#9A968B] hover:text-red-600 font-medium px-2.5 py-1.5 ml-auto transition-colors"
                    data-testid="delete-plan-btn"
                  >
                    <Trash size={14} weight="bold" /> Rimuovi
                  </button>
                </div>
              </div>
              {floorPlans.length > 1 && (
                <div className="mt-6 flex gap-2 overflow-x-auto hide-scroll">
                  {floorPlans.map((p) => (
                    <button key={p.id} onClick={() => setActivePlanId(p.id)}
                      className={`w-20 h-20 flex-shrink-0 overflow-hidden border-2 bg-white ${activePlanId === p.id ? "border-[#7C8B6E]" : "border-transparent"}`}
                      data-testid={`plan-thumb-${p.id}`}>
                      <img src={authedFileUrl(p.original_path)} alt="" className="w-full h-full object-contain" />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="card-flat h-[58vh] flex flex-col items-center justify-center text-center px-6" data-testid="plan-empty-state">
              <div className="w-14 h-14 rounded-full bg-[#F1F4ED] border border-[#D8DECE] flex items-center justify-center mb-4">
                <House size={26} weight="light" className="text-[#7C8B6E]" />
              </div>
              <h3 className="font-editorial text-[28px] sm:text-[32px] leading-[1.1] text-[#1C1C1A]">
                Nessuna pianta ancora.
              </h3>
              <p className="mt-3 text-[13.5px] sm:text-[14px] text-[#6B6A65] max-w-md leading-relaxed">
                Carica la <strong className="text-[#1C1C1A]">pianta catastale</strong> (foto o JPG dalla visura) — Pronto la legge, riconosce le stanze e te la restituisce <strong className="text-[#1C1C1A]">arredata 2D in stile editoriale</strong>. Pronta per portali e Story.
              </p>
              {/* iter v25 — rimosso il bottone "Carica planimetrie" duplicato:
                  l'utente vedeva 2 "Carica" (uno qui + uno in sidebar).
                  Ora il singolo CTA sta nella sidebar a sinistra. */}
              <div className="mt-6 text-[12px] text-[#7C8B6E] font-semibold flex items-center gap-1.5">
                ← Usa <strong>Carica planimetria</strong> nella colonna a sinistra
              </div>
            </div>
          )}
        </div>
      </div>
  );
}
